import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  CircularProgress,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  makeStyles,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import {
  History as HistoryIcon,
  Description as FileIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import BackupPreviewModal from "../BackupPreviewModal";

const useStyles = makeStyles((theme) => ({
  backupItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedItem: {
    backgroundColor: theme.palette.action.selected,
    borderColor: theme.palette.primary.main,
  },
  fileInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  noBackups: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  resultBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
}));

const MergeBackupModal = ({ open, onClose, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [backups, setBackups] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);
  
  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewBackupId, setPreviewBackupId] = useState(null);
  
  const handleOpenPreview = (e, backupId) => {
    e.stopPropagation(); // Prevent selecting the backup
    setPreviewBackupId(backupId);
    setPreviewModalOpen(true);
  };

  useEffect(() => {
    if (open && ticket?.id) {
      fetchAvailableBackups();
    }
  }, [open, ticket?.id]);

  const fetchAvailableBackups = async () => {
    setLoading(true);
    setMergeResult(null);
    setSelectedBackup(null);
    try {
      const { data } = await api.get(`/whatsapp-backups/ticket/${ticket.id}/available`);
      setBackups(data.backups || []);
    } catch (err) {
      toastError(err);
      setBackups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBackup = (backup) => {
    setSelectedBackup(backup.id === selectedBackup?.id ? null : backup);
    setMergeResult(null);
  };

  const handleMerge = async () => {
    if (!selectedBackup) {
      toast.warning(i18n.t("mergeBackupModal.selectBackup"));
      return;
    }

    setMerging(true);
    setMergeResult(null);
    try {
      // First parse the backup if not already parsed
      if (selectedBackup.status !== "parsed") {
        await api.post(`/whatsapp-backups/${selectedBackup.id}/parse`);
      }

      // Restore backup to this ticket's contact
      const { data } = await api.post(`/whatsapp-backups/${selectedBackup.id}/restore`, {
        whatsappId: ticket.whatsappId,
        customerPhoneNumber: selectedBackup.phoneNumber,
      });

      setMergeResult({
        success: true,
        ...data,
      });

      toast.success(i18n.t("mergeBackupModal.success"));
    } catch (err) {
      setMergeResult({
        success: false,
        error: err.response?.data?.error || err.message,
      });
      toastError(err);
    } finally {
      setMerging(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon color="primary" />
          <span>{i18n.t("mergeBackupModal.title")}</span>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : backups.length === 0 ? (
          <Box className={classes.noBackups}>
            <FileIcon style={{ fontSize: 48, opacity: 0.5 }} />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              {i18n.t("mergeBackupModal.noBackups")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("mergeBackupModal.noBackupsHint")}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {i18n.t("mergeBackupModal.selectBackupHint")}
            </Typography>
            <List>
              {backups.map((backup) => (
                <ListItem
                  key={backup.id}
                  button
                  onClick={() => handleSelectBackup(backup)}
                  className={`${classes.backupItem} ${
                    selectedBackup?.id === backup.id ? classes.selectedItem : ""
                  }`}
                >
                  <ListItemIcon>
                    <FileIcon color={selectedBackup?.id === backup.id ? "primary" : "action"} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box className={classes.fileInfo}>
                        <span>{backup.fileName}</span>
                        <Chip
                          size="small"
                          label={backup.fileFormat.toUpperCase()}
                          className={classes.chip}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        {i18n.t("mergeBackupModal.phone")}: {backup.phoneNumber} • {formatFileSize(backup.fileSize)} • {formatDate(backup.createdAt)}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={i18n.t("whatsappBackups.buttons.preview") || "Preview Chat"}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleOpenPreview(e, backup.id)}
                        style={{ color: "#2196f3" }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {mergeResult && (
          <Box className={classes.resultBox}>
            {mergeResult.success ? (
              <>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CheckIcon style={{ color: "green" }} />
                  <Typography variant="subtitle1" style={{ color: "green" }}>
                    {i18n.t("mergeBackupModal.mergeComplete")}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {i18n.t("mergeBackupModal.messagesCreated")}: {mergeResult.messagesCreated}
                </Typography>
                <Typography variant="body2">
                  {i18n.t("mergeBackupModal.messagesSkipped")}: {mergeResult.messagesSkipped}
                </Typography>
              </>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <ErrorIcon style={{ color: "red" }} />
                <Typography variant="body2" style={{ color: "red" }}>
                  {mergeResult.error}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("mergeBackupModal.close")}
        </Button>
        <Button
          onClick={handleMerge}
          color="primary"
          variant="contained"
          disabled={!selectedBackup || merging || backups.length === 0}
          startIcon={merging ? <CircularProgress size={16} /> : <HistoryIcon />}
        >
          {merging ? i18n.t("mergeBackupModal.merging") : i18n.t("mergeBackupModal.merge")}
        </Button>
      </DialogActions>
      
      {/* Backup Preview Modal - opens on top of this modal */}
      <BackupPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        backupId={previewBackupId}
        onRestore={null} // No restore from here - just view
      />
    </Dialog>
  );
};

export default MergeBackupModal;














